/* eslint-disable no-unused-expressions */
import scrollTo from './scrollTo';
import ResizeTracker from 'utils/ResizeTracker';


/**
 * @module SectionScroll
 */
class SectionScroll {

    /**
     * @param {HTMLElement} element - The HTMLElement this module is constructed upon
     * @param {Object} options - externally added options
     */

    constructor(element, options) {
        this._element = element;
        this._options = {
            ...options,
            hash: null,
            mobile: 768,
            desktopOffset: 210,
            mobileOffset: 140,
            animationDuration: 1200
        };

        this._state = {
            distance: 0,
            observer: null,
            isIntersecting: false
        };

        this._setupObserver = this._setupObserver.bind(this);
        this._handleClicked = this._handleClicked.bind(this);
        this._setOffset = this._setOffset.bind(this);

        this._cacheSelectors();
        if (this._link) {
            this._setOptions();
            this._setOffset();
            this._setResizeTracker();
            this._addEventListeners();
            this._setupObserver();
        }

    }

    /**
     * Cache selectors
     *
     * @memberof SectionScroll
     */
    _cacheSelectors() {
        this._link = this._element.querySelector('a');
        this._header = document.querySelector('.header__img');
        this._rootContainer = document.body;
    }

    /**
     * Set hash options
     *
     * @memberof SectionScroll
     */
    _setOptions() {
        this._options.hash = this._link.getAttribute('href').replace('#', '');
    }

    /**
     * Set container offset
     *
     * @memberof SectionScroll
     */
    _setOffset() {
        const headerOffset = this._header.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        const offset = windowWidth > this._options.mobile ? this._options.desktopOffset : this._options.mobileOffset;
        this._state.distance = headerOffset - offset;
    }


    /**
     * Set ResizeTracker
     *
     * @memberof SectionScroll
     */
    _setResizeTracker() {
        this._resizeTracker = new ResizeTracker();

        this._resizeTracker.init(
            { element: this._rootContainer },
            this._setOffset
        );
    }

    /**
     * Add listeners
     *
     * @memberof SectionScroll
     */
    _addEventListeners() {
        this._link.addEventListener('click', event => this._handleClicked(event));

    }

    /**
    * Scroll on click
    *
    * @memberof SectionScroll
    * @param {*} event
    */
    _handleClicked(event) {
        event.preventDefault();
        const box = this._element.getBoundingClientRect();
        // eslint-disable-next-line no-magic-numbers
        const top = (window.pageYOffset || document.documentElement.scrollTop) - (document.documentElement.clientTop || 0);
        scrollTo(event, box.top + this._state.distance + top);
    }

    /**
    * Set intersection observer on sections
    *
    * @memberof SectionScroll
    */
    _setupObserver() {
        const options = {
            root: document.body,
            rootMargin: '20px',
            threshold: 0.5
        };

        this._state.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                // eslint-disable-next-line no-magic-numbers
                const isIntersecting = entry.isIntersecting && entry.intersectionRatio > 0.01;
                isIntersecting && !this._state.isIntersecting && this._triggerIntersection();

            });
        }, options);

        this._state.observer.POLL_INTERVAL = 100;

        this._state.observer.observe(this._element);

    }

    /**
     * Remove intersection observer
     *
     * @memberof SectionScroll
     */
    _removeObserver() {
        this._state.observer && this._state.observer.unobserve(this._element);
    }

    /**
     * Add transition to the section
     *
     * @memberof SectionScroll
     */
    _triggerIntersection() {
        this._state.isIntersecting = true;
        this._element.setAttribute('data-move', '');

        const timeout = setTimeout(() => {
            this._element.removeAttribute('data-move', '');
            clearTimeout(timeout);
        }, this._options.animationDuration);

        this._removeObserver();
    }

}

export default SectionScroll;
/* eslint-enable */
